import 'svelte/internal/disclose-version';
import * as $ from 'svelte/internal/client';
import { getSplit, deleteExpense } from '$lib/api/splits';
import { Button } from '$lib/components/ui/button';
import * as Card from '$lib/components/ui/card';
import ConfirmDialog from '$lib/components/ui/confirm-dialog/confirm-dialog.svelte';
import ExpenseEditModal from '$lib/components/expense/ExpenseEditModal.svelte';
import { addToast } from '$lib/stores/toastStore.svelte';
import { route, navigate } from '$lib/router';
import { Plus, Pencil, Trash2, Receipt, ListFilter, X } from '$lib/icons';
import SplitPageHeader from '$lib/components/ui/split-page-header/SplitPageHeader.svelte';
import { getCachedSplitName, setCachedSplitName } from '$lib/stores/splitTitleCache';

var root_2 = $.from_html(`<div class="flex flex-col items-center justify-center py-12 space-y-4"><svg class="animate-spin h-8 w-8 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg> <p class="text-muted-foreground">Loading expenses...</p></div>`);
var root_5 = $.from_html(`<div class="w-full flex items-center gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-800"><span class="flex-1">This split is settled — expenses are read-only.</span> <button class="underline font-medium whitespace-nowrap">View settlement</button></div>`);
var root_7 = $.from_html(`<option> </option>`);
var root_8 = $.from_html(`<option> </option>`);
var root_6 = $.from_html(`<section class="w-full" aria-label="Expense filters"><div class="flex items-center gap-2"><!> <select aria-label="Filter by payer" class="flex-1 h-9 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"><option>All payers</option><!></select> <select aria-label="Filter by beneficiary" class="flex-1 h-9 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"><option>All beneficiaries</option><!></select> <!></div></section>`);
var root_14 = $.from_html(`<!> Add Expense`, 1);
var root_15 = $.from_html(`<div class="flex flex-col items-center justify-center py-12 space-y-3"><!> <h2 class="text-lg font-semibold text-muted-foreground">No expenses yet</h2> <p class="text-sm text-muted-foreground">Tap + to add your first expense</p></div>`);
var root_17 = $.from_html(`<div class="flex flex-col items-center justify-center py-12 space-y-3"><!> <h2 class="text-lg font-semibold text-muted-foreground">No matching expenses</h2> <p class="text-sm text-muted-foreground">Try adjusting your filters</p> <!></div>`);
var root_23 = $.from_html(`<p class="text-xs text-muted-foreground mb-2 leading-relaxed"> </p>`);
var root_24 = $.from_html(`<div class="flex items-center justify-end gap-1"><!> <!></div>`);
var root_22 = $.from_html(`<div class="flex items-start justify-between mb-1"><span data-testid="expense-description" class="font-bold text-sm"> </span> <span class="text-sm font-semibold text-primary"> </span></div> <div class="flex items-center justify-between mb-2"><span class="text-xs text-muted-foreground"> </span> <span class="text-xs text-muted-foreground"> </span></div> <div class="flex items-center mb-1"><span class="text-xs text-muted-foreground"> </span></div> <!> <!>`, 1);
var root_19 = $.from_html(`<div class="w-full space-y-3"></div>`);
var root_4 = $.from_html(`<!> <!> <section class="w-full"><div class="bg-teal-50/50 border border-teal-200 rounded-lg px-4 py-3"><div class="flex items-center justify-between"><span class="text-sm font-medium"><!></span> <span class="text-sm font-semibold text-primary"> </span></div></div></section> <!> <!>`, 1);
var root = $.from_html(`<div class="flex flex-col items-center space-y-4 w-full max-w-[520px] mx-auto"><!> <!></div> <!> <!> <!>`, 1);

export default function ExpenseList($$anchor, $$props) {
	$.push($$props, true);

	// Expense List Screen - Story FNS-002-5
	const splitId = $.derived(() => route.params.splitId || '');

	// State
	let split = $.state(null);

	let splitDisplayName = $.state($.proxy(getCachedSplitName(route.params.splitId || '')));
	const isSettled = $.derived(() => $.get(split)?.settlement != null);
	let isLoading = $.state(true);
	let showLoading = $.state(false);
	let loadingTimer = null;

	// Delete confirmation state
	let showDeleteConfirm = $.state(false);

	let expenseToDelete = $.state(null);
	let isDeleting = $.state(false);

	// Add expense modal state
	let showAddExpense = $.state(false);

	// Edit expense modal state
	let showEditExpense = $.state(false);

	let expenseToEdit = $.state(null);

	// Filter state — initialized from URL query params, then managed as local state
	let selectedPayer = $.state($.proxy(String(route.search?.payer || '')));

	let selectedBeneficiary = $.state($.proxy(String(route.search?.beneficiary || '')));
	const hasActiveFilters = $.derived(() => !!$.get(selectedPayer) || !!$.get(selectedBeneficiary));

	// Derived values
	const sortedExpenses = $.derived(() => $.get(split)?.expenses
		? [...$.get(split).expenses].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
		: []);

	const filteredExpenses = $.derived(() => $.get(sortedExpenses).filter((expense) => {
		if ($.get(selectedPayer) && expense.payerId !== $.get(selectedPayer)) return false;

		if ($.get(selectedBeneficiary)) {
			const activeShares = expense.splitMode === 'FREE'
				? expense.shares.filter((s) => s.parts != null && s.parts > 0)
				: expense.shares;

			if (!activeShares.some((s) => s.participantId === $.get(selectedBeneficiary))) return false;
		}

		return true;
	}));

	const totalExpenseCount = $.derived(() => $.get(sortedExpenses).length);
	const expenseCount = $.derived(() => $.get(filteredExpenses).length);
	const expenseTotal = $.derived(() => $.get(filteredExpenses).reduce((sum, e) => sum + e.amount, 0));

	// Load split data
	$.user_effect(() => {
		if ($.get(splitId)) {
			loadSplit($.get(splitId));
		}
	});

	async function loadSplit(id) {
		$.set(isLoading, true);
		$.set(showLoading, false);

		if (loadingTimer) clearTimeout(loadingTimer);

		loadingTimer = setTimeout(
			() => {
				$.set(showLoading, true);
			},
			500
		);

		try {
			$.set(split, await getSplit(id), true);
			$.set(splitDisplayName, $.get(split).name, true);
			setCachedSplitName(id, $.get(split).name);
		} catch(err) {
			const apiError = err;

			if (apiError.status === 404 || apiError.status === 400) {
				addToast({ type: 'info', message: 'Split not found — create a new one.' });
				navigate('/');
			} else {
				addToast({
					type: 'error',
					message: apiError.detail || 'Failed to load expenses'
				});
			}
		} finally {
			$.set(isLoading, false);
			$.set(showLoading, false);

			if (loadingTimer) {
				clearTimeout(loadingTimer);
				loadingTimer = null;
			}
		}
	}

	function formatCurrency(amount) {
		return new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR' }).format(amount);
	}

	function formatDate(dateString) {
		const date = new Date(dateString);
		const now = new Date();
		const diffMs = now.getTime() - date.getTime();
		const diffHours = diffMs / (1000 * 60 * 60);

		// Handle future dates defensively
		if (diffMs < 0) {
			return date.toLocaleDateString('en-IE', { month: 'short', day: 'numeric' });
		}

		if (diffHours < 1) return 'Just now';
		if (diffHours < 24) return `${Math.floor(diffHours)}h ago`;
		if (diffHours < 48) return 'Yesterday';

		return date.toLocaleDateString('en-IE', { month: 'short', day: 'numeric' });
	}

	function splitModeIcon(mode) {
		switch (mode) {
			case 'BY_NIGHT':
				return '\u{1F319}';

			case 'EQUAL':
				return '\u229C';

			case 'BY_SHARE':
				return '\u{1F465}';

			case 'FREE':
				return '\u270F\uFE0F';
		}
	}

	function splitModeText(mode) {
		switch (mode) {
			case 'BY_NIGHT':
				return 'By Night';

			case 'EQUAL':
				return 'Equal';

			case 'BY_SHARE':
				return 'By Members';

			case 'FREE':
				return 'Manual';
		}
	}

	function getPayerName(payerId) {
		return $.get(split)?.participants.find((p) => p.id === payerId)?.name || 'Unknown';
	}

	function getParticipantSharesText(expense) {
		if (!$.get(split)) return '';

		const activeShares = expense.shares.filter((s) => s.amount > 0);

		return activeShares.map((s) => {
			const name = $.get(split).participants.find((p) => p.id === s.participantId)?.name || 'Unknown';

			return `${name}: ${formatCurrency(s.amount)}`;
		}).join(' · ');
	}

	function updateFilterUrl(payer, beneficiary) {
		const url = new URL(window.location.href);

		if (payer) {
			url.searchParams.set('payer', payer);
		} else {
			url.searchParams.delete('payer');
		}

		if (beneficiary) {
			url.searchParams.set('beneficiary', beneficiary);
		} else {
			url.searchParams.delete('beneficiary');
		}

		window.history.replaceState(null, '', url.toString());
	}

	function handlePayerFilterChange(payerId) {
		$.set(selectedPayer, payerId, true);
		updateFilterUrl(payerId, $.get(selectedBeneficiary));
	}

	function handleBeneficiaryFilterChange(participantId) {
		$.set(selectedBeneficiary, participantId, true);
		updateFilterUrl($.get(selectedPayer), participantId);
	}

	function clearFilters() {
		$.set(selectedPayer, '');
		$.set(selectedBeneficiary, '');
		updateFilterUrl('', '');
	}

	function handleDeleteClick(expense) {
		if (!expense || !expense.id) {
			addToast({ type: 'error', message: 'Invalid expense' });

			return;
		}

		$.set(expenseToDelete, expense, true);
		$.set(showDeleteConfirm, true);
	}

	async function handleConfirmDelete() {
		if (!$.get(expenseToDelete)) return;

		$.set(isDeleting, true);

		try {
			await deleteExpense($.get(splitId), $.get(expenseToDelete).id);

			addToast({
				type: 'success',
				message: 'Expense deleted',
				description: `${$.get(expenseToDelete).description} · €${$.get(expenseToDelete).amount.toFixed(2)} · Paid by ${getPayerName($.get(expenseToDelete).payerId)}`
			});

			$.set(showDeleteConfirm, false);
			$.set(expenseToDelete, null);
			await loadSplit($.get(splitId));
		} catch(err) {
			const error = err;

			addToast({
				type: 'error',
				message: error.detail || 'Failed to delete expense'
			});
		} finally {
			$.set(isDeleting, false);
		}
	}

	function handleCancelDelete() {
		$.set(showDeleteConfirm, false);
		$.set(expenseToDelete, null);
	}

	function handleAddExpense() {
		$.set(showAddExpense, true);
	}

	function handleCloseAddExpense() {
		$.set(showAddExpense, false);
	}

	async function handleAddExpenseSuccess() {
		await loadSplit($.get(splitId));
	}

	function handleEditClick(expense) {
		if (!expense || !expense.id) {
			addToast({ type: 'error', message: 'Invalid expense' });

			return;
		}

		$.set(expenseToEdit, expense, true);
		$.set(showEditExpense, true);
	}

	function handleCloseEditExpense() {
		$.set(showEditExpense, false);
		$.set(expenseToEdit, null);
	}

	async function handleEditExpenseSuccess() {
		await loadSplit($.get(splitId));
	}

	var fragment = root();
	var div = $.first_child(fragment);
	var node = $.child(div);

	{
		var consequent = ($$anchor) => {
			SplitPageHeader($$anchor, {
				get splitName() {
					return $.get(splitDisplayName);
				},

				get splitId() {
					return $.get(splitId);
				}
			});
		};

		$.if(node, ($$render) => {
			if ($.get(splitId)) $$render(consequent);
		});
	}

	var node_1 = $.sibling(node, 2);

	{
		var consequent_1 = ($$anchor) => {
			var div_1 = root_2();

			$.append($$anchor, div_1);
		};

		var alternate_3 = ($$anchor) => {
			var fragment_2 = $.comment();
			var node_2 = $.first_child(fragment_2);

			{
				var consequent_11 = ($$anchor) => {
					var fragment_3 = root_4();
					var node_3 = $.first_child(fragment_3);

					{
						var consequent_2 = ($$anchor) => {
							var div_2 = root_5();
							var button = $.sibling($.child(div_2), 2);

							button.__click = () => navigate(`/splits/${$.get(splitId)}/settlement`);
							$.reset(div_2);
							$.append($$anchor, div_2);
						};

						$.if(node_3, ($$render) => {
							if ($.get(isSettled)) $$render(consequent_2);
						});
					}

					var node_4 = $.sibling(node_3, 2);

					{
						var consequent_4 = ($$anchor) => {
							var section = root_6();
							var div_3 = $.child(section);
							var node_5 = $.child(div_3);

							ListFilter(node_5, { class: 'h-4 w-4 text-muted-foreground shrink-0' });

							var select = $.sibling(node_5, 2);

							select.__change = (e) => handlePayerFilterChange(e.currentTarget.value);

							var option = $.child(select);

							option.value = option.__value = '';

							var node_6 = $.sibling(option);

							$.each(node_6, 17, () => $.get(split).participants, $.index, ($$anchor, participant) => {
								var option_1 = root_7();
								var text = $.child(option_1, true);

								$.reset(option_1);

								var option_1_value = {};

								$.template_effect(() => {
									$.set_text(text, $.get(participant).name);

									if (option_1_value !== (option_1_value = $.get(participant).id)) {
										option_1.value = (option_1.__value = $.get(participant).id) ?? '';
									}
								});

								$.append($$anchor, option_1);
							});

							$.reset(select);

							var select_value;

							$.init_select(select);

							var select_1 = $.sibling(select, 2);

							select_1.__change = (e) => handleBeneficiaryFilterChange(e.currentTarget.value);

							var option_2 = $.child(select_1);

							option_2.value = option_2.__value = '';

							var node_7 = $.sibling(option_2);

							$.each(node_7, 17, () => $.get(split).participants, $.index, ($$anchor, participant) => {
								var option_3 = root_8();
								var text_1 = $.child(option_3, true);

								$.reset(option_3);

								var option_3_value = {};

								$.template_effect(() => {
									$.set_text(text_1, $.get(participant).name);

									if (option_3_value !== (option_3_value = $.get(participant).id)) {
										option_3.value = (option_3.__value = $.get(participant).id) ?? '';
									}
								});

								$.append($$anchor, option_3);
							});

							$.reset(select_1);

							var select_1_value;

							$.init_select(select_1);

							var node_8 = $.sibling(select_1, 2);

							{
								var consequent_3 = ($$anchor) => {
									Button($$anchor, {
										variant: 'ghost',
										size: 'sm',
										onclick: clearFilters,
										class: 'min-h-[36px] min-w-[36px] p-1',
										'aria-label': 'Clear filters',

										children: ($$anchor, $$slotProps) => {
											X($$anchor, { class: 'h-4 w-4' });
										},

										$$slots: { default: true }
									});
								};

								$.if(node_8, ($$render) => {
									if ($.get(hasActiveFilters)) $$render(consequent_3);
								});
							}

							$.reset(div_3);
							$.reset(section);

							$.template_effect(() => {
								if (select_value !== (select_value = $.get(selectedPayer))) {
									(
										select.value = (select.__value = $.get(selectedPayer)) ?? '',
										$.select_option(select, $.get(selectedPayer))
									);
								}

								if (select_1_value !== (select_1_value = $.get(selectedBeneficiary))) {
									(
										select_1.value = (select_1.__value = $.get(selectedBeneficiary)) ?? '',
										$.select_option(select_1, $.get(selectedBeneficiary))
									);
								}
							});

							$.append($$anchor, section);
						};

						$.if(node_4, ($$render) => {
							if ($.get(split).participants.length > 0) $$render(consequent_4);
						});
					}

					var section_1 = $.sibling(node_4, 2);
					var div_4 = $.child(section_1);
					var div_5 = $.child(div_4);
					var span = $.child(div_5);
					var node_9 = $.child(span);

					{
						var consequent_5 = ($$anchor) => {
							var text_2 = $.text();

							$.template_effect(() => $.set_text(text_2, `${$.get(expenseCount) ?? ''} of ${$.get(totalExpenseCount) ?? ''} ${$.get(totalExpenseCount) === 1 ? 'expense' : 'expenses'}`));
							$.append($$anchor, text_2);
						};

						var alternate = ($$anchor) => {
							var text_3 = $.text();

							$.template_effect(() => $.set_text(text_3, `${$.get(expenseCount) ?? ''} total ${$.get(expenseCount) === 1 ? 'expense' : 'expenses'}`));
							$.append($$anchor, text_3);
						};

						$.if(node_9, ($$render) => {
							if ($.get(hasActiveFilters)) $$render(consequent_5); else $$render(alternate, false);
						});
					}

					$.reset(span);

					var span_1 = $.sibling(span, 2);
					var text_4 = $.child(span_1);

					$.reset(span_1);
					$.reset(div_5);
					$.reset(div_4);
					$.reset(section_1);

					var node_10 = $.sibling(section_1, 2);

					{
						var consequent_6 = ($$anchor) => {
							Button($$anchor, {
								onclick: handleAddExpense,
								variant: 'outline',
								class: 'w-full min-h-[44px]',

								children: ($$anchor, $$slotProps) => {
									var fragment_9 = root_14();
									var node_11 = $.first_child(fragment_9);

									Plus(node_11, { class: 'h-4 w-4 mr-1' });
									$.next();
									$.append($$anchor, fragment_9);
								},

								$$slots: { default: true }
							});
						};

						$.if(node_10, ($$render) => {
							if (!$.get(isSettled)) $$render(consequent_6);
						});
					}

					var node_12 = $.sibling(node_10, 2);

					{
						var consequent_7 = ($$anchor) => {
							var div_6 = root_15();
							var node_13 = $.child(div_6);

							Receipt(node_13, { class: 'h-12 w-12 text-muted-foreground/50' });
							$.next(4);
							$.reset(div_6);
							$.append($$anchor, div_6);
						};

						var alternate_2 = ($$anchor) => {
							var fragment_10 = $.comment();
							var node_14 = $.first_child(fragment_10);

							{
								var consequent_8 = ($$anchor) => {
									var div_7 = root_17();
									var node_15 = $.child(div_7);

									ListFilter(node_15, { class: 'h-12 w-12 text-muted-foreground/50' });

									var node_16 = $.sibling(node_15, 6);

									Button(node_16, {
										variant: 'outline',
										size: 'sm',
										onclick: clearFilters,

										children: ($$anchor, $$slotProps) => {
											$.next();

											var text_5 = $.text('Clear filters');

											$.append($$anchor, text_5);
										},

										$$slots: { default: true }
									});

									$.reset(div_7);
									$.append($$anchor, div_7);
								};

								var alternate_1 = ($$anchor) => {
									var div_8 = root_19();

									$.each(div_8, 21, () => $.get(filteredExpenses), (expense) => expense.id, ($$anchor, expense) => {
										var fragment_11 = $.comment();
										var node_17 = $.first_child(fragment_11);

										$.component(node_17, () => Card.Root, ($$anchor, Card_Root) => {
											Card_Root($$anchor, {
												class: 'w-full',

												children: ($$anchor, $$slotProps) => {
													var fragment_12 = $.comment();
													var node_18 = $.first_child(fragment_12);

													$.component(node_18, () => Card.Content, ($$anchor, Card_Content) => {
														Card_Content($$anchor, {
															class: 'py-3 px-4',

															children: ($$anchor, $$slotProps) => {
																var fragment_13 = root_22();
																var div_9 = $.first_child(fragment_13);
																var span_2 = $.child(div_9);
																var text_6 = $.child(span_2, true);

																$.reset(span_2);

																var span_3 = $.sibling(span_2, 2);
																var text_7 = $.child(span_3, true);

																$.reset(span_3);
																$.reset(div_9);

																var div_10 = $.sibling(div_9, 2);
																var span_4 = $.child(div_10);
																var text_8 = $.child(span_4);

																$.reset(span_4);

																var span_5 = $.sibling(span_4, 2);
																var text_9 = $.child(span_5, true);

																$.reset(span_5);
																$.reset(div_10);

																var div_11 = $.sibling(div_10, 2);
																var span_6 = $.child(div_11);
																var text_10 = $.child(span_6);

																$.reset(span_6);
																$.reset(div_11);

																var node_19 = $.sibling(div_11, 2);

																{
																	var consequent_9 = ($$anchor) => {
																		var p_1 = root_23();
																		var text_11 = $.child(p_1, true);

																		$.reset(p_1);
																		$.template_effect(($0) => $.set_text(text_11, $0), [() => getParticipantSharesText($.get(expense))]);
																		$.append($$anchor, p_1);
																	};

																	$.if(node_19, ($$render) => {
																		if (getParticipantSharesText($.get(expense))) $$render(consequent_9);
																	});
																}

																var node_20 = $.sibling(node_19, 2);

																{
																	var consequent_10 = ($$anchor) => {
																		var div_12 = root_24();
																		var node_21 = $.child(div_12);

																		Button(node_21, {
																			variant: 'ghost',
																			size: 'sm',
																			onclick: () => handleEditClick($.get(expense)),
																			class: 'min-h-[44px] min-w-[44px]',

																			get 'aria-label'() {
																				return `Edit expense: ${$.get(expense).description ?? ''}`;
																			},

																			children: ($$anchor, $$slotProps) => {
																				Pencil($$anchor, { class: 'h-4 w-4' });
																			},

																			$$slots: { default: true }
																		});

																		var node_22 = $.sibling(node_21, 2);

																		Button(node_22, {
																			variant: 'ghost',
																			size: 'sm',
																			onclick: () => handleDeleteClick($.get(expense)),
																			class: 'min-h-[44px] min-w-[44px] text-destructive hover:text-destructive',

																			get 'aria-label'() {
																				return `Delete expense: ${$.get(expense).description ?? ''}`;
																			},

																			children: ($$anchor, $$slotProps) => {
																				Trash2($$anchor, { class: 'h-4 w-4' });
																			},

																			$$slots: { default: true }
																		});

																		$.reset(div_12);
																		$.append($$anchor, div_12);
																	};

																	$.if(node_20, ($$render) => {
																		if (!$.get(isSettled)) $$render(consequent_10);
																	});
																}

																$.template_effect(
																	($0, $1, $2, $3, $4) => {
																		$.set_text(text_6, $.get(expense).description);
																		$.set_text(text_7, $0);
																		$.set_text(text_8, `Paid by ${$1 ?? ''}`);
																		$.set_text(text_9, $2);

																		$.set_text(text_10, `${$3 ?? ''}
                  ${$4 ?? ''}`);
																	},
																	[
																		() => formatCurrency($.get(expense).amount),
																		() => getPayerName($.get(expense).payerId),
																		() => formatDate($.get(expense).createdAt),
																		() => splitModeIcon($.get(expense).splitMode),
																		() => splitModeText($.get(expense).splitMode)
																	]
																);

																$.append($$anchor, fragment_13);
															},

															$$slots: { default: true }
														});
													});

													$.append($$anchor, fragment_12);
												},

												$$slots: { default: true }
											});
										});

										$.append($$anchor, fragment_11);
									});

									$.reset(div_8);
									$.append($$anchor, div_8);
								};

								$.if(
									node_14,
									($$render) => {
										if ($.get(filteredExpenses).length === 0) $$render(consequent_8); else $$render(alternate_1, false);
									},
									true
								);
							}

							$.append($$anchor, fragment_10);
						};

						$.if(node_12, ($$render) => {
							if ($.get(sortedExpenses).length === 0) $$render(consequent_7); else $$render(alternate_2, false);
						});
					}

					$.template_effect(($0) => $.set_text(text_4, `${$0 ?? ''} total`), [() => formatCurrency($.get(expenseTotal))]);
					$.append($$anchor, fragment_3);
				};

				$.if(
					node_2,
					($$render) => {
						if (!$.get(isLoading) && $.get(split)) $$render(consequent_11);
					},
					true
				);
			}

			$.append($$anchor, fragment_2);
		};

		$.if(node_1, ($$render) => {
			if ($.get(showLoading)) $$render(consequent_1); else $$render(alternate_3, false);
		});
	}

	$.reset(div);

	var node_23 = $.sibling(div, 2);

	ConfirmDialog(node_23, {
		get open() {
			return $.get(showDeleteConfirm);
		},

		title: 'Delete this expense?',
		description: 'This will recalculate balances.',
		confirmLabel: 'Delete',
		cancelLabel: 'Cancel',

		get isLoading() {
			return $.get(isDeleting);
		},

		onConfirm: handleConfirmDelete,
		onCancel: handleCancelDelete
	});

	var node_24 = $.sibling(node_23, 2);

	{
		var consequent_12 = ($$anchor) => {
			ExpenseEditModal($$anchor, {
				get open() {
					return $.get(showAddExpense);
				},

				get splitId() {
					return $.get(splitId);
				},

				preselectedPayerId: null,

				get participants() {
					return $.get(split).participants;
				},

				onClose: handleCloseAddExpense,
				onSuccess: handleAddExpenseSuccess
			});
		};

		$.if(node_24, ($$render) => {
			if ($.get(split)) $$render(consequent_12);
		});
	}

	var node_25 = $.sibling(node_24, 2);

	{
		var consequent_13 = ($$anchor) => {
			ExpenseEditModal($$anchor, {
				get open() {
					return $.get(showEditExpense);
				},

				get splitId() {
					return $.get(splitId);
				},

				get expense() {
					return $.get(expenseToEdit);
				},

				get participants() {
					return $.get(split).participants;
				},

				onClose: handleCloseEditExpense,
				onSuccess: handleEditExpenseSuccess
			});
		};

		$.if(node_25, ($$render) => {
			if ($.get(split)) $$render(consequent_13);
		});
	}

	$.append($$anchor, fragment);
	$.pop();
}

$.delegate(['click', 'change']);
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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